import React from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import useSearchPreferences from 'hooks/use-search-preferences';
import useTranslation from 'hooks/use-translation';
import { useDurationEstimates } from 'hooks/use-duration-estimates';
import { openInfoForDispensary } from 'shared/core/helpers/dispensaries';
import { convertMetersToMiles } from 'shared/helpers/address';
import Imgix from 'shared/components/imgix';
import Link from 'components/core/link';
import routes from 'src/routes';

export function DispensaryCardRedesign({ dispensary }) {
  const { t } = useTranslation();
  const SearchPreferences = useSearchPreferences();
  const isPickup = useObserver(() => SearchPreferences.isPickup);
  const orderType = useObserver(() => SearchPreferences.orderType);
  const { durationEstimates } = useDurationEstimates(dispensary);

  const { name, listImage, distance, recDispensary, medicalDispensary, offerPickup, offerDelivery } = dispensary;

  const openInfo = openInfoForDispensary(dispensary);
  const isOpen = _.some(openInfo, `isOpen`);

  const milesFromUser = Math.round(convertMetersToMiles(Number(distance))) || `<1`;
  const milesLabel = milesFromUser === 1 ? `mile` : `miles`;

  const lowInMinutes = durationEstimates[orderType]?.lowInMinutes;
  const highInMinutes = durationEstimates[orderType]?.highInMinutes;
  const showEstimates =
    Number.isFinite(lowInMinutes) &&
    Number.isFinite(highInMinutes) &&
    isOpen &&
    !(isPickup ? false : dispensary.featureFlags?.hideDeliveryEstimate);

  const estimateLabel = isPickup
    ? t('dispensaryCard.readyIn', 'Ready in {{low}}-{{high}} mins', { low: lowInMinutes, high: highInMinutes })
    : t('dispensaryCard.deliveryIn', '{{low}}-{{high}} min delivery', { low: lowInMinutes, high: highInMinutes });

  let typeLabel = t('common.medical', 'Medical');
  if (recDispensary && medicalDispensary) {
    typeLabel = t('common.recAndMed', 'Rec & Med');
  } else if (recDispensary) {
    typeLabel = t('common.recreational', 'Recreational');
  }

  return (
    <CardLink href={`${routes.DISPENSARY}/${dispensary.cName}`} route={routes.DISPENSARY_CNAME}>
      <Card data-testid='dispensary-card-redesign'>
        <ImageSection>
          <CardImage src={listImage} htmlAttributes={{ alt: name }} height={200} width={400} />
          <ImageOverlay />
          <ImageBadges>
            <StatusBadge $open={isOpen}>{isOpen ? t('common.open', 'Open') : t('common.closed', 'Closed')}</StatusBadge>
            <TypeBadge>{typeLabel}</TypeBadge>
          </ImageBadges>
        </ImageSection>

        <CardBody>
          <DispensaryName>{name}</DispensaryName>

          <MetaRow>
            <MetaText>
              {milesFromUser} {milesLabel} away
            </MetaText>
          </MetaRow>

          {showEstimates && (
            <EstimateRow>
              <EstimateDot />
              <EstimateText>{estimateLabel}</EstimateText>
            </EstimateRow>
          )}

          <OrderTypePills>
            {offerPickup && <OrderTypePill>{t('common.pickup', 'Pickup')}</OrderTypePill>}
            {offerDelivery && <OrderTypePill>{t('common.delivery', 'Delivery')}</OrderTypePill>}
          </OrderTypePills>

          <OrderButton>{t('common.orderNow', 'Order now')} →</OrderButton>
        </CardBody>
      </Card>
    </CardLink>
  );
}

const CardLink = styled(Link)`
  text-decoration: none;
  display: block;
`;

const Card = styled.div`
  background: var(--rds-bg-card);
  border: 1px solid var(--rds-border);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(233, 185, 73, 0.3);
  }
`;

const ImageSection = styled.div`
  position: relative;
  height: 180px;
  background: var(--rds-input-bg);
  flex-shrink: 0;
`;

const CardImage = styled(Imgix)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.45) 100%);
`;

const ImageBadges = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $open }) => ($open ? `var(--rds-gold)` : `rgba(0,0,0,0.55)`)};
  color: ${({ $open }) => ($open ? `var(--rds-text-on-gold)` : `var(--rds-cream)`)};
  backdrop-filter: blur(4px);
`;

const TypeBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(77, 42, 61, 0.85);
  color: var(--rds-cream);
  backdrop-filter: blur(4px);
`;

const CardBody = styled.div`
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const DispensaryName = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--rds-cream);
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaText = styled.span`
  font-size: 13px;
  color: var(--rds-cream-dim);
`;

const EstimateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EstimateDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--rds-gold);
  flex-shrink: 0;
`;

const EstimateText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--rds-gold);
`;

const OrderTypePills = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const OrderTypePill = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--rds-cream-dim);
`;

const OrderButton = styled.div`
  margin-top: 4px;
  padding: 10px 16px;
  background: var(--rds-gold);
  color: var(--rds-text-on-gold);
  font-weight: 700;
  font-size: 14px;
  border-radius: 8px;
  text-align: center;
  transition: background 0.15s ease;

  ${Card}:hover & {
    background: var(--rds-gold-bright);
  }
`;
